// Run types
export interface Run {
  id: string;
  partitionKey: string;
  title: string;
  description?: string;
  createdBy: string;
  tenantId?: string;
  ownerId?: string;
  organiserId?: string;
  delegates?: string[];
  vendor?: string;
  runType?: string;
  createdAt: string;
  pickupTime?: string;
  pickupLocation?: string;
  status: RunStatus;
  isPublic: boolean;
  shareCode: string;
  orders: Order[];
  receipt?: Receipt;
  totalAmount: number;
  orderCount: number;
  timestamp: number;
}

export enum RunStatus {
  Open = 'Open',
  Collecting = 'Collecting',
  Ordered = 'Ordered',
  PickedUp = 'PickedUp',
  Completed = 'Completed',
  Cancelled = 'Cancelled',
}

// Order types
export interface Order {
  id: string;
  runId: string;
  customerName: string;
  customerEmail?: string;
  ownerId?: string;
  personKey?: string;
  items: OrderItem[];
  specialInstructions?: string;
  amount: number;
  createdAt: string;
  status: OrderStatus;
  delivered?: boolean;
}

export interface OrderItem {
  id: string;
  name: string;
  size?: string;
  customizations: string[];
  price: number;
  quantity: number;
}

export enum OrderStatus {
  Pending = 'Pending',
  Confirmed = 'Confirmed',
  Prepared = 'Prepared',
  Ready = 'Ready',
  Delivered = 'Delivered',
  Cancelled = 'Cancelled',
}

// API Request types
export interface CreateRunRequest {
  title: string;
  description?: string;
  pickupTime?: string;
  pickupLocation?: string;
  isPublic?: boolean;
}

export interface AddOrderRequest {
  customerName: string;
  customerEmail?: string;
  items: OrderItem[];
  specialInstructions?: string;
}

// API Response types
export interface RunSummary {
  runId: string;
  title: string;
  status: string;
  orderCount: number;
  totalAmount: number;
  createdAt: string;
  pickupTime?: string;
  pickupLocation?: string;
  orders: OrderSummary[];
  groupedItems: GroupedItem[];
}

export interface OrderSummary {
  id: string;
  customerName: string;
  amount: number;
  itemCount: number;
  status: string;
  createdAt: string;
}

export interface GroupedItem {
  name: string;
  size?: string;
  quantity: number;
  totalPrice: number;
}

// Form types
export interface RunFormData {
  title: string;
  description: string;
  pickupTime: string;
  pickupLocation: string;
  isPublic: boolean;
}

export interface OrderFormData {
  customerName: string;
  customerEmail: string;
  items: OrderItemFormData[];
  specialInstructions: string;
}

export interface OrderItemFormData {
  name: string;
  size: string;
  customizations: string[];
  price: number;
  quantity: number;
}

// Receipt types
export interface Receipt {
  vendor?: string;
  total?: number;
  date?: string;
  imageUrl?: string;
  ocrText?: string;
  uploadedAt: string;
}

// API Error types
export interface ApiError {
  error: string;
}

// Common response wrapper
export interface ApiResponse<T> {
  data?: T;
  error?: string;
}

// Delegation types
export interface GraphUser {
  id: string;
  displayName: string;
  email: string;
  jobTitle?: string;
  department?: string;
}

export interface AddDelegateRequest {
  delegateOid: string;
}

// Person Profile and Usuals types
export interface PersonProfile {
  id: string;
  ownerId: string;
  personKey: string;
  displayName: string;
  email?: string;
  usuals: Usual[];
  counts: PersonCounts;
  updatedAt: string;
}

export interface Usual {
  key: string;
  label: string;
  item: string;
  size?: string;
  options: string[];
  vendor?: string;
  runType?: string;
  lastOrderedAt: string;
  count90d: number;
  count30d: number;
  totalCount: number;
}

export interface PersonCounts {
  '30d': number;
  '90d': number;
  all: number;
}

// History types
export interface RunListItem {
  id: string;
  title: string;
  vendor?: string;
  runType?: string;
  orderCount: number;
  totalAmount: number;
  createdAt: string;
  status: string;
}

export interface HistoryResponse {
  runs: RunListItem[];
  total: number;
  offset: number;
  limit: number;
}

// Search and suggestion types
export interface PersonSearchResult {
  id: string;
  displayName: string;
  email: string;
  lastOrderedAt: string;
}

export interface UsualSuggestion {
  key: string;
  label: string;
  item: string;
  size?: string;
  options: string[];
  vendor?: string;
  score: number;
}
